import { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";
import axiosClient from "../api/axiosClient";
import "../styles/AdminGraph.css";

const PASS_THRESHOLD = 50;

/**
 * Converts raw test names into short readable labels.
 * "CUSTOM_C_20260428_1427_3767"       → "C · 28 Apr 14:27"
 * "CUSTOM_PYTHON_20260511_1632_2940"  → "PYTHON · 11 May 16:32"
 * "C_20260428_162230_24582a0e"        → "C · 28 Apr 16:22"
 */
function formatTestName(testName) {
  try {
    const parts = testName.split("_");
    const isCustom = parts[0] === "CUSTOM";
    const course  = isCustom ? parts[1] : parts[0];
    const dateStr = isCustom ? parts[2] : parts[1];
    const timeStr = isCustom ? parts[3] : parts[2];

    const year  = dateStr.slice(0, 4);
    const month = dateStr.slice(4, 6);
    const day   = dateStr.slice(6, 8);
    const hh    = timeStr.slice(0, 2);
    const mm    = timeStr.slice(2, 4);

    const dateObj  = new Date(`${year}-${month}-${day}`);
    const dayLabel = dateObj.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

    return `${course} · ${dayLabel} ${hh}:${mm}`;
  } catch {
    return testName;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
export default function TestAnalysisChart({ userid, moduleName }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  // courseCache: { [courseName]: testScoreTrend[] }
  const [courseCache,     setCourseCache]     = useState({});
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourse,  setSelectedCourse]  = useState("");
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState(null);

  // ── On mount: fetch every course in parallel and cache results ────────────
 useEffect(() => {
  if (!userid || !moduleName) return;
  console.log("moduleName prop:", moduleName); 
   console.log("userid:", userid);

  const fetchAllCourses = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("admintoken");

      // Split "C, Python" → ["C", "Python"]
      const courses = Array.isArray(moduleName) ? moduleName : moduleName.split(",").map((s) => s.trim());
      console.log("courses array:", courses);

      // Fetch all courses in parallel
      const parallelResults = await Promise.allSettled(
        courses.map((course) =>
          axiosClient.get(`/auth/admin/user-detail/userIdAndModuleName`, {
            params:  { userId: userid, moduleName: course },
            headers: { Authorization: `${token}` },
          })
        )
      );

      // Build cache
      const cache = {};
      courses.forEach((course, idx) => {
        const result = parallelResults[idx];
        cache[course] =
          result.status === "fulfilled"
            ? result.value.data?.testScoreTrend ?? []
            : [];
      });

      setCourseCache(cache);
      setAvailableCourses(courses);
      setSelectedCourse(courses[0] ?? "");
    } catch (err) {
      console.log(err);
        console.log("Error response:", err?.response?.data); 
      setError("Failed to load test data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  fetchAllCourses();
}, [userid, moduleName]);

  // ── Derive chart data from selected course in cache ───────────────────────
  const testScoreTrend = courseCache[selectedCourse] ?? [];
  const labels         = testScoreTrend.map((t) => formatTestName(t.testName));
  const scores         = testScoreTrend.map((t) => parseFloat(t.score.toFixed(1)));
  const passCount      = scores.filter((s) => s >= PASS_THRESHOLD).length;
  const failCount      = scores.length - passCount;
  const avgScore       =
    scores.length > 0
      ? (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
      : 0;

  // ── Draw / redraw chart whenever selected course data changes ─────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (chartRef.current) chartRef.current.destroy();

    // Show empty state if no data
    if (scores.length === 0) return;

    const ctx = canvas.getContext("2d");

    const passGrad = ctx.createLinearGradient(0, 0, 0, 260);
    passGrad.addColorStop(0, "rgba(244,160,160,0.45)");
    passGrad.addColorStop(1, "rgba(244,160,160,0.02)");

    const failGrad = ctx.createLinearGradient(0, 0, 0, 260);
    failGrad.addColorStop(0, "rgba(75,74,207,0.35)");
    failGrad.addColorStop(1, "rgba(75,74,207,0.02)");

    chartRef.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: "Test pass",
            data: scores.map((s) => (s >= PASS_THRESHOLD ? s : null)),
            borderColor: "#f4a0a0",
            borderWidth: 2.5,
            pointRadius: 4,
            pointBackgroundColor: "#f4a0a0",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
            tension: 0.45,
            fill: true,
            backgroundColor: passGrad,
            spanGaps: false,
          },
          {
            label: "Test fail",
            data: scores.map((s) => (s < PASS_THRESHOLD ? s : null)),
            borderColor: "#4b4acf",
            borderWidth: 2.5,
            pointRadius: 4,
            pointBackgroundColor: "#4b4acf",
            pointBorderColor: "#fff",
            pointBorderWidth: 1.5,
            tension: 0.45,
            fill: true,
            backgroundColor: failGrad,
            spanGaps: false,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: "#fff",
            titleColor: "#333",
            bodyColor: "#555",
            borderColor: "#e0e0ef",
            borderWidth: 1,
            padding: 10,
            callbacks: {
              title: (items) => {
                const idx = items[0].dataIndex;
                return `${labels[idx]}  •  Score: ${scores[idx]}%`;
              },
              label: (item) => ` ${item.dataset.label}`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              font: { size: 11 },
              color: "#888",
              maxRotation: 30,
              minRotation: 20,
            },
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: "rgba(180,178,210,0.25)", lineWidth: 1 },
            border: { display: false },
            ticks: {
              stepSize: 20,
              callback: (v) => v + "%",
              font: { size: 12 },
              color: "#888",
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) chartRef.current.destroy();
    };
  }, [selectedCourse, courseCache]);

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="chart-wrap chart-wrap--center">
        <div className="chart-spinner" />
        <p className="chart-status-text">Loading test data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-wrap chart-wrap--center">
        <p className="chart-status-text chart-status-text--error">{error}</p>
      </div>
    );
  }

  return (
    <div className="chart-wrap">

      {/* ── Header ── */}
      <div className="chart-header">
        <span className="chart-title">Test analysis</span>
        <div className="legend">
          <span className="legend-item">
            <span className="leg-dot leg-dot--pass" />
            Test pass
          </span>
          <span className="legend-item">
            <span className="leg-dot leg-dot--fail" />
            Test fail
          </span>
        </div>
      </div>

      {/* ── Course selector — populated from API's selectedCourses ── */}
      <div className="course-select-row">
        <svg className="course-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
          viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
        <div className="select-wrapper">
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="course-select"
          >
            {availableCourses.map((course) => (
              <option key={course} value={course}>
                Course: {course}
              </option>
            ))}
          </select>
          <svg className="select-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12"
            viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* ── Summary stats ── */}
      <div className="stats-row">
        <div className="stat-card">
          <span className="stat-label">Total tests</span>
          <span className="stat-value">{scores.length}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Avg score</span>
          <span className="stat-value">{avgScore}%</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Passed</span>
          <span className="stat-value stat-value--pass">{passCount}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Failed</span>
          <span className={`stat-value ${failCount > 0 ? "stat-value--fail" : ""}`}>
            {failCount}
          </span>
        </div>
      </div>

      {/* ── Chart canvas or empty state ── */}
      <div className="canvas-wrapper">
        {scores.length === 0 ? (
          <div className="chart-empty">
            <p>No test data available for <strong>{selectedCourse}</strong></p>
          </div>
        ) : (
          <canvas
            ref={canvasRef}
            role="img"
            aria-label="Line chart showing test pass and fail scores across tests."
          >
            Test pass and fail score trends.
          </canvas>
        )}
      </div>

      {/* ── Test pill badges ── */}
      {/* <div className="test-labels-row">
        {testScoreTrend.map((t) => (
          <div
            key={t.testName}
            className={`test-pill ${t.score >= PASS_THRESHOLD ? "test-pill--pass" : "test-pill--fail"}`}
            title={t.testName}
          >
            <span className="test-pill-label">{formatTestName(t.testName)}</span>
            <span className="test-pill-score">{t.score}%</span>
          </div>
        ))}
      </div> */}
    </div>
  );
}